import { useEffect } from "react";
import { useButtonStore } from "../../stores/components/button";
import { useInvoiceStore } from "../../stores/invoice";
import { Select } from "../ui/inputs/Select";
import { getInvoicesTemplates } from "../../api/invoicesTemplates";
import { useCrmStore } from "../../stores/crm";
import { showToastError, showToastSuccess } from "../../services/toast";
import {
  createProforma,
  getCustomerFiles,
  getMissions,
  getOneCustomerFile,
} from "../../api/customerFile";
import VisuCreateDocument from "./VisuCreateDocument";
import { formatDateForInput, replacePointByComma } from "../../services/function";
import {
  createCreditNote,
  getInvoice,
  getProforma,
  updateProforma,
} from "../../api/invoices";
import { SelectText } from "../ui/inputs/SelectText";
import { Checkbox } from "../ui/inputs/Checkbox";
import {
  getCreditNote,
  updateCreditNote,
} from "../../api/creditNotes";
import { useLoaderStore } from "../../stores/components/loader";
import Loader from "../ui/Loader";
import { ICreateCreditNote, ICreateInvoice } from "../../interfaces/invoices";
import { ActionButton, DetailHeader } from "../ui/page/PageShell";

const Proforma = () => {
  // Store
  const {
    tabInvoice,
    setTabInvoice,
    formData,
    setFormData,
    setDetailCustomer,
    detailsInvoice,
    setDetailsInvoice,
    resetInvoice,
    viewOrUpdate,
    setViewOrUpdate,
    idProformaSelected,
    idsProformasSelected,
    setIdsProformasSelected,
    invoiceDetails,
    setInvoiceDetails,
    totalSold,
    setTotalSold,
    totalHT,
  } = useInvoiceStore();

  const {
    docModels,
    setDocModels,
    clientFiles,
    setClientFiles,
    missions,
    setMissions,
  } = useCrmStore();
  const { setCreateButton } = useButtonStore();
  const { isLoading, setIsLoading } = useLoaderStore();

  // Récupère les informations de la facture selectionnée
  useEffect(() => {
    if (tabInvoice === "Factures" && idsProformasSelected.length > 0) {
      getInvoice(idsProformasSelected[0])
        .then((res) => {
          setInvoiceDetails(res.data);
          setFormData({ exonerated: res.data.exonerated });
          setFormData({ issueDate: formatDateForInput(res.data.issueDate) });
          setFormData({ orderNumber: res.data.orderNumber });
          if (res.data.idCustomerFile) {
            getOneCustomerFile(res.data.idCustomerFile)
            .then((res) => {
              setDetailCustomer(res.data);
            })
            .catch(() => {
              showToastError("Une erreur est survenue");
            });
          }
          if (res.data.exonerated) {
            setTotalSold(Number(res.data.balance) || 0);
          } else {
            const totalWithoutTva = res.data.balance / 1.2;
            setTotalSold(Number(totalWithoutTva));
          }
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  }, []);

  // Récupère les modèles de documents et les dossiers clients
  useEffect(() => {
    getInvoicesTemplates()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        setDocModels(formattedData);
      })
      .catch(() => showToastError("Une erreur est survenue"));

    getCustomerFiles()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));

        setClientFiles(formattedData);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  }, []);

  // Récupère les informations de la proforma selectionnée
  useEffect(() => {
    if (idProformaSelected !== 0 && tabInvoice === "Proformas") {
      getProforma(idProformaSelected)
        .then((res) => {

          const data = res.data;
          // Formatage des dates pour affichage dans les inputs
          data.issueDate = formatDateForInput(data.issueDate);
          // Ajoutes les détails de la proforma
          setDetailsInvoice(data.billingItems);

          setFormData(data);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    } else if (idProformaSelected !== 0 && tabInvoice === "Avoirs") {
      getCreditNote(Number(idProformaSelected))
        .then((res) => {
          const data = res.data;
          // Le solde restant HT - le total HT de la proforma
          setTotalSold(Number(res.data?.invoice?.balance) - Number(totalHT));

          // Formatage des dates pour affichage dans les inputs
          data.issueDate = formatDateForInput(data.issueDate);
          setInvoiceDetails(res.data);
          // Ajoutes les détails de la proforma
          setDetailsInvoice(data.billingItems);
          setFormData(data);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  }, [idProformaSelected, viewOrUpdate]);

  useEffect(() => {
    if (formData.idCustomerFile) {
      // Récupère les informations du client selectionné
      getOneCustomerFile(formData.idCustomerFile)
        .then((res) => {
          setDetailCustomer(res.data);
          
          // Si on est en mode modification, on ne modifie pas le champ exonéré
          if(viewOrUpdate !== "update") {
            setFormData({
              ...formData,
              exonerated: res.data.exonerated,
            });
          }
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });

      // Récupère les missions du client selectionné non cloturées
      getMissions(formData.idCustomerFile)
        .then((res) => {
          const sortDatabyClosed = res.data.filter(
            (el: any) => el.closed === false
          );
          const formattedData = sortDatabyClosed?.map((data: any) => ({
            label: data.department + " - " + data.startDate + (data.endDate ? " - " + data.endDate : ""), 
            value: data.id,
            idTemplate: data.idInvoiceTemplate,
            idDepartment: data.idDepartment,
          }));
          // Affiche les missions ouvertes en premier
          setMissions(formattedData);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });

    }
  }, [formData.idCustomerFile]);

  // Gestion des changements de formulaire
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };

  const handleSelectChangeMission = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedOption = e.target.options[e.target.selectedIndex];
    const idTemplate = selectedOption.dataset.idTemplate; // Access using dataset

    setFormData({
      ...formData,
      [e.target.name]: parseInt(e.target.value),
      idInvoiceTemplate: idTemplate ? parseInt(idTemplate) : null,
    });
  };

  const handleSelectTextChange = (selectedOption: any) => {
    if (selectedOption) {
      setFormData({ ...formData, idCustomerFile: selectedOption.value });
    } else {
      setFormData({ ...formData, idCustomerFile: null });
    }
  };

  const handleChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  // Fonction permettant de créer une proforma ou une proforma d'avoir
  const handleSubmitCreate = () => {
    const transformedDetailsInvoice = detailsInvoice.map((item) => ({
      ...item,
      text: replacePointByComma(item.text),
      amount: typeof item.amount === 'string' 
        ? parseFloat(item.amount.replace(',', '.')) || 0
        : parseFloat(item.amount) || 0,
    }));

    const data: ICreateInvoice = {
      issueDate: formData.issueDate,
      idCustomerFile: formData.idCustomerFile,
      exonerated: formData.exonerated,
      idMission: formData.idMission,
      idInvoiceTemplate: parseInt(formData.idInvoiceTemplate),
      orderNumber: formData.orderNumber,
      commentary: formData.commentary,
      expense: formData.expense,
      billingItems: transformedDetailsInvoice,
    };

    const dataCreditNote: ICreateCreditNote = {
      ...formData,
      issueDate: formData.issueDate || new Date().toISOString().split('T')[0],
      billingItems: transformedDetailsInvoice,
    };

    if (tabInvoice === "Proformas" && viewOrUpdate === "") {
      // Création de la proforma
      setIsLoading(true);
      createProforma(formData.idCustomerFile, formData.idMission, data)
        .then(() => {
          showToastSuccess("Proforma créée avec succès");
          setCreateButton(false);
          resetInvoice();
          setIsLoading(false);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
          setIsLoading(false);
        });
    } else if (tabInvoice === "Factures" && viewOrUpdate === "") {
      // Création de l'avoir
      if (totalHT > invoiceDetails.amountExclVAT) {
        showToastError(
          "Le montant de l'avoir ne peut pas être supérieur au solde restant de la facture"
        );
        return;
      }
      setIsLoading(true);
      createCreditNote(idsProformasSelected[0], dataCreditNote)
        .then(() => {
          showToastSuccess("Avoir créé avec succès");
          setIdsProformasSelected([]);
          setTabInvoice("Avoirs");
          setCreateButton(false);
          setIsLoading(false);
          resetInvoice();
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
          setIsLoading(false);
        });
    } else if (tabInvoice === "Proformas" && viewOrUpdate !== "") {
      // Modification de la proforma
      updateProforma(idProformaSelected, data)
        .then(() => {
          showToastSuccess("Proforma modifiée avec succès");
          setCreateButton(false);
          resetInvoice();
          setViewOrUpdate("");
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    } else if (tabInvoice === "Avoirs" && viewOrUpdate !== "") {
      // Modification de l'avoir
      updateCreditNote(Number(idProformaSelected), dataCreditNote)
        .then(() => {
          showToastSuccess("Avoir modifié avec succès");
          setCreateButton(false);
          resetInvoice();
          setViewOrUpdate("");
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  };

  // Gestion du solde restant
  useEffect(() => {
      let balanceHT;
      if (invoiceDetails.exonerated) {
        // Si la facture est exonérée de TVA, le solde restant est le solde restant HT
        if(tabInvoice === "Factures") {
          balanceHT = Number(invoiceDetails.balance) || 0;
        } else if(tabInvoice === "Avoirs") {
          balanceHT = Number(invoiceDetails?.invoice?.balance) || 0;
        }
      } else {
        if(tabInvoice === "Factures") {
          balanceHT = Number(invoiceDetails.balance) / 1.2;
        } else if(tabInvoice === "Avoirs") {
          balanceHT = Number(invoiceDetails?.invoice?.balance / 1.2) || 0;
        }
        // Si la facture n'est pas exonérée de TVA, le solde restant est le solde restant HT divisé par 1.2
      }

      if(isNaN(totalHT)) {
        setTotalSold(Number(balanceHT));
      }
      // Calcul du solde restant HT pour creer un avoir sur facture
      if (tabInvoice === "Factures") {
        const total = Number(balanceHT) - Number(totalHT);
        if (total < 0) {
          showToastError(
            "Le montant de l'avoir ne peut pas être supérieur au solde restant de la facture"
          );
        }
        setTotalSold(Number(total));
      } else if (tabInvoice === "Avoirs") {
        // Le solde restant HT - le total HT de la proforma
        const total = Number(balanceHT) - Number(totalHT);  
        if (total < 0) {
          showToastError(
            "Le montant de l'avoir ne peut pas être supérieur au solde restant de la facture"
          );
        }
        setTotalSold(Number(total));
      }
  }, [totalHT]); 

  // Gestion des honoraires mission sociale pour ajouter le texte
  useEffect(() => {
    if(formData.idMission) {
      const mission = missions?.find((mission: any) => mission?.value === formData.idMission);

      if(mission?.idDepartment === 3 || mission?.idDepartment === 8) {
        setDetailsInvoice([{
          text: viewOrUpdate !== "" ? detailsInvoice[0].text : "Honoraires mission sociale :",
          amount: viewOrUpdate === "" ? "0" : detailsInvoice[0].amount
        }])
      }
    }
  },[formData.idMission])
  
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <DetailHeader
        title={
          tabInvoice === "Proformas" && viewOrUpdate === ""
            ? "Création d'une proforma"
            : tabInvoice === "Proformas" && viewOrUpdate !== ""
            ? "Modification d'une proforma"
            : tabInvoice === "Factures" && viewOrUpdate === ""
            ? "Création d'un avoir"
            : tabInvoice === "Avoirs" && viewOrUpdate !== ""
            ? "Modification d'un avoir"
            : null
        }
        hint={`Merci de renseigner les informations nécessaires pour la création de ${
          tabInvoice === "Proformas" ? "la proforma" : "l' avoir"
        }.`}
        onClose={() => {
          setCreateButton(false);
          setViewOrUpdate("");
          resetInvoice();
        }}
        actions={
          <ActionButton variant="primary" type="submit" form="proformaForm">
            {viewOrUpdate === "" ? "Créer" : "Modifier"}
          </ActionButton>
        }
      />

      <div className="flex min-h-0 w-full flex-1 items-start overflow-hidden p-3 sm:p-4">
        <form
          className="flex h-full min-h-0 w-full gap-3 overflow-hidden"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitCreate();
          }}
          id="proformaForm"
        >
          {!isLoading ? (
            <>
              <div className="flex h-full min-h-0 w-1/3 flex-col items-center overflow-y-auto rounded-xl border border-slate-200 bg-white p-3">
                {tabInvoice !== "Factures" && tabInvoice !== "Avoirs" ? (
                  <>
                    <SelectText
                      label="Dossier"
                      name="idCustomerFile"
                      onChange={handleSelectTextChange}
                      value={formData.idCustomerFile}
                      fetchOptions={clientFiles}
                      className="cursor-pointer w-full p-2"
                    />
                    <Select
                      label="Mission"
                      name="idMission"
                      value={formData.idMission || ""}
                      onChange={handleSelectChangeMission}
                      fetchOptions={missions}
                      className="w-full p-2"
                    />
                    <Select
                      label="Model de document"
                      name="idInvoiceTemplate"
                      value={formData.idInvoiceTemplate || ""}
                      onChange={handleSelectChange}
                      fetchOptions={docModels}
                      className="w-full p-2"
                    />
                    <div className="flex flex-col justify-start items-start w-full p-2">
                      <Checkbox
                        label="Frais"
                        name="expense"
                        onChange={handleChangeCheckbox}
                        checked={formData.expense}
                        value={formData.expense}
                        clasName="text-sm"
                      />
                      {tabInvoice === "Proformas" ? (
                        <Checkbox
                          label="Exonéré de TVA"
                          name="exonerated"
                          onChange={handleChangeCheckbox}
                          checked={formData.exonerated}
                          value={formData.exonerated}
                          clasName="text-sm mt-2"
                        />
                      ) : null}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col justify-start items-start w-full p-2">
                    <p className="font-thin text-sm">
                      Dossier client :{" "}
                      <span className="font-bold">
                        {invoiceDetails.customerFile}
                      </span>
                    </p>
                    <p className="font-thin text-sm mt-2">
                      Mission :{" "}
                      <span className="font-bold">
                        {invoiceDetails.nameDepartment}{" "}
                        {invoiceDetails.issueDate}
                      </span>
                    </p>
                    <p className="font-thin text-sm mt-2">
                      Solde restant HT :{" "}
                      <span className="font-bold">
                        {totalSold?.toFixed(2)} €
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex h-full min-h-0 w-2/3 items-start justify-center overflow-hidden rounded-xl border border-slate-200 bg-white p-3">
                <div className="max-h-full w-full overflow-y-auto">
                  <VisuCreateDocument />
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Loader />
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Proforma;
